import React from "react";
import Modal from "../../../common/Modal";
import type {
  TerminationEmployeeSnapshot,
  TerminationRequestDto,
  TerminationSupportingDocumentRef,
} from "../../Utils/types";
import { formatExitDateDisplay } from "../../Utils/exitDateUtils";
import { terminationListStatusLabel } from "../../Utils/terminationStatusUtils";
import { extractFilenameFromUrl } from "../../Utils/attachmentUtils";

export interface TerminationViewModalProps {
  record: TerminationRequestDto | null;
  onClose: () => void;
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  const isEmpty =
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "");
  const displayValue = isEmpty ? "—" : value;

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
        {label}
      </span>
      <div className="text-[14px] font-medium text-slate-800 whitespace-pre-wrap break-words leading-snug">
        {displayValue}
      </div>
    </div>
  );
}

function pickStr(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const t = v.trim();
  return t || undefined;
}

function nameFromObject(v: unknown): string | undefined {
  if (!v || typeof v !== "object") return undefined;
  const o = v as Record<string, unknown>;
  return (
    pickStr(o.functionalManagerName) ??
    pickStr(o.fullName) ??
    pickStr(o.name) ??
    pickStr(o.displayName)
  );
}

/** FM label from nested employee / user shapes (GET /v2/termination/:id). */
function terminationFunctionalManagerDisplay(
  record: TerminationRequestDto,
): string {
  const emp = record.employee as
    | (TerminationEmployeeSnapshot & Record<string, unknown>)
    | undefined;
  if (emp) {
    const direct = pickStr(emp.functionalManagerName);
    if (direct) return direct;
    const snake = pickStr(emp.functional_manager_name);
    if (snake) return snake;
    const fm = emp.functionalManager ?? emp.functional_manager;
    const asStr = pickStr(typeof fm === "string" ? fm : undefined);
    if (asStr) return asStr;
    const fromObj = nameFromObject(fm);
    if (fromObj) return fromObj;
    const user = emp.user;
    if (user && typeof user === "object") {
      const u = user as Record<string, unknown>;
      const fromUser =
        pickStr(u.functionalManagerName) ??
        pickStr(u.functional_manager_name) ??
        pickStr(typeof u.functionalManager === "string" ? u.functionalManager : undefined) ??
        nameFromObject(u.functionalManager ?? u.functional_manager);
      if (fromUser) return fromUser;
    }
  }
  const r = record as Record<string, unknown>;
  const root =
    pickStr(r.functionalManagerName) ??
    pickStr(r.functional_manager_name) ??
    pickStr(typeof r.functionalManager === "string" ? r.functionalManager : undefined) ??
    nameFromObject(r.functionalManager ?? r.functional_manager);
  return root ?? "—";
}

function documentLinksList(docs: TerminationSupportingDocumentRef[]) {
  if (docs.length === 0) return null;
  return (
    <ul className="list-disc pl-5 text-sm text-primary-600 space-y-1 m-0">
      {docs.map((d, i) => {
        const href = typeof d.fileUrl === "string" ? d.fileUrl : undefined;
        const label =
          (typeof d.originalName === "string" && d.originalName.trim()) ||
          (href ? extractFilenameFromUrl(href) : `Document ${i + 1}`);
        return (
          <li key={`${href ?? label}-${i}`}>
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline font-semibold"
              >
                {label}
              </a>
            ) : (
              label
            )}
          </li>
        );
      })}
    </ul>
  );
}

const TerminationViewModal: React.FC<TerminationViewModalProps> = ({
  record,
  onClose,
}) => {
  const emp = record?.employee;
  const docs = record?.supportingDocuments ?? [];
  const pipDocs = record?.warningPipDocuments ?? [];

  return (
    <Modal
      isOpen={!!record}
      onClose={onClose}
      title="Termination details"
      size="4xl"
    >
      {record ? (
        <div className="w-full pb-2 pt-1">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
            <div className="space-y-5">
                <DetailRow label="Employee Name" value={emp?.fullName} />
                <DetailRow label="Employee Id" value={emp?.employeeCode} />
                <DetailRow label="Email" value={emp?.workEmail} />
                <DetailRow label="Department" value={emp?.department} />
                <DetailRow label="Designation" value={emp?.designation} />
                <DetailRow
                  label="Reporting manager"
                  value={
                    emp?.reportingManagerName?.trim()
                      ? emp.reportingManagerName
                      : "—"
                  }
                />
                <DetailRow
                  label="Functional manager"
                  value={terminationFunctionalManagerDisplay(record)}
                />
              </div>

            <div className="space-y-5">
              <DetailRow
                label="Termination status"
                value={terminationListStatusLabel(record)}
              />
              <DetailRow
                label="Termination type"
                value={record.terminationType?.displayName ?? "—"}
              />
              <DetailRow
                label="Last working date"
                value={
                  record.lastWorkingDay
                    ? formatExitDateDisplay(record.lastWorkingDay)
                    : "—"
                }
              />
              <DetailRow
                label="Reason / justification"
                value={record.reasonJustification}
              />
              {pipDocs.length > 0 ? (
                <DetailRow
                  label="Warning / PIP references"
                  value={documentLinksList(pipDocs)}
                />
              ) : null}
              {record.confidentialityNote ? (
                <DetailRow
                  label="Confidentiality note"
                  value={record.confidentialityNote}
                />
              ) : null}
              <DetailRow
                label="Submitted On"
                value={
                  record.createdAt
                    ? formatExitDateDisplay(record.createdAt)
                    : "—"
                }
              />
            </div>
          </div>

          {/* {record.workflow ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-3">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Workflow
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                <DetailRow
                  label="Status label"
                  value={record.workflow.workflowStatusLabel ?? "—"}
                />
                <DetailRow
                  label="Workflow status"
                  value={record.workflow.workflowStatus ?? "—"}
                />
                <DetailRow
                  label="Approval instance"
                  value={record.workflow.approvalInstanceId ?? "—"}
                />
              </div>
            </div>
          ) : null} */}

          {docs.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Supporting documents
              </h3>
              {documentLinksList(docs)}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
};

export default TerminationViewModal;
